import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Layout";
import { PageHeader, Breadcrumbs, Overline } from "@/components/Bits";
import api from "@/lib/api";
import { Info, ShieldCheck, ScrollText, Users, ExternalLink } from "lucide-react";

function ContentPage({ title, overline, subtitle, sections, crumb, note, noindex = false }) {
  return (
    <>
      <Seo title={title} description={subtitle} noindex={noindex} />
      <PageHeader overline={overline} title={title} subtitle={subtitle} />
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: crumb || title }]} />
        {note && <div className="rounded-md bg-secondary p-4 text-sm flex gap-2 mb-8"><Info size={16} className="text-accent shrink-0 mt-0.5" /><p className="text-muted-foreground">{note}</p></div>}
        <div className="space-y-8">
          {sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-display font-bold text-xl mb-2">{s.h}</h2>
              {Array.isArray(s.p) ? s.p.map((p, j) => <p key={j} className="text-muted-foreground leading-relaxed mb-2">{p}</p>) : <p className="text-muted-foreground leading-relaxed">{s.p}</p>}
              {s.list && <ul className="mt-2 space-y-1.5 text-muted-foreground list-disc pl-5">{s.list.map((li, j) => <li key={j}>{li}</li>)}</ul>}
            </section>
          ))}
        </div>
      </div>
    </>
  );
}

export function About() {
  return <ContentPage overline="Quiénes somos" title="Hallo Suiza" crumb="Quiénes somos"
    subtitle="Una plataforma para hispanohablantes que aprenden alemán para vivir y trabajar en Suiza."
    sections={[
      { h: "Nuestra misión", p: "Ayudarte a comunicarte con confianza en la Suiza germanófona. Enseñamos alemán estándar con vocabulario suizo y una introducción progresiva al alemán suizo, siempre con explicaciones claras en español." },
      { h: "Qué nos diferencia", p: "No creamos contenido masivo de baja calidad. Priorizamos situaciones reales, ejercicios verificados y guías revisadas por personas antes de publicarse." },
      { h: "Lo que no hacemos", p: "No ofrecemos asesoramiento legal, fiscal, migratorio ni médico. No inventamos experiencias, estadísticas ni fuentes. Cuando falta información real, lo indicamos claramente." },
    ]} />;
}

export function EditorialMethodology() {
  return <ContentPage overline="Metodología editorial" title="Cómo creamos y revisamos el contenido" crumb="Metodología editorial"
    subtitle="Nuestro proceso editorial garantiza utilidad, precisión y revisión humana."
    sections={[
      { h: "Proceso por estados", p: "Cada contenido pasa por estados sucesivos antes de publicarse:", list: ["Idea", "En preparación", "Borrador inicial", "Pendiente de revisión lingüística", "Pendiente de revisión factual", "Aprobado", "Programado", "Publicado"] },
      { h: "Revisión humana obligatoria", p: "Ningún borrador se publica automáticamente. Antes de aprobar comprobamos: alemán y traducciones revisadas, respuestas de ejercicios verificadas, información contrastada, fuentes válidas, contenido original, sin afirmaciones inventadas, imagen con derechos, metadatos completos, enlaces comprobados y aprobación humana." },
      { h: "Fuentes y confianza", p: "Cuando una guía trata sobre trámites, empleo, residencia o servicios, consultamos y enlazamos preferentemente fuentes oficiales suizas, mostramos la fecha de comprobación y recomendamos consultar al organismo competente. Resumimos con palabras propias y no copiamos de otras webs." },
    ]} />;
}

export function CorrectionsPolicy() {
  return <ContentPage overline="Política de correcciones" title="Cómo corregimos errores" crumb="Política de correcciones"
    subtitle="Nos tomamos en serio la exactitud. Si detectas un error, te lo agradeceremos."
    sections={[
      { h: "Cómo reportar", p: "Puedes escribirnos desde la página de contacto indicando la guía o lección y el error detectado." },
      { h: "Cómo actuamos", p: "Revisamos el aviso, verificamos la información con fuentes fiables y, si procede, corregimos el contenido indicando la fecha de última revisión." },
      { h: "Transparencia", p: "Cuando una corrección afecta a información relevante, dejamos constancia de la fecha de actualización en la propia guía." },
    ]} />;
}

export function LivingInSwitzerland() {
  return (
    <>
      <Seo title="Vivir en Suiza" description="Recursos y guías para hispanohablantes que viven o van a vivir en la Suiza germanófona." />
      <PageHeader overline="Vivir en Suiza" title="Recursos para tu vida en Suiza"
        subtitle="Guías prácticas sobre alemán, trabajo, vivienda y vida cotidiana. La información es general y puede cambiar; consulta siempre el organismo oficial competente." />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Vivir en Suiza" }]} />
        <div className="grid sm:grid-cols-2 gap-4">
          {[["Guías prácticas", "Contenido revisado para tu día a día.", "/guias"], ["Glosario", "Términos clave explicados.", "/glosario"], ["Alemán para trabajar", "Vocabulario por sector profesional.", "/aleman-para-trabajar"], ["Herramientas", "Practica gratis sin registrarte.", "/herramientas"]].map(([t, d, to]) => (
            <Link key={to} to={to} className="rounded-lg border border-border bg-card p-6 hover:-translate-y-1 hover:border-primary/40 transition-transform">
              <h2 className="font-display font-semibold text-lg">{t}</h2><p className="mt-2 text-sm text-muted-foreground">{d}</p>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}

export function Privacy() {
  return <ContentPage overline="Legal" title="Política de privacidad" crumb="Privacidad"
    subtitle="Cómo tratamos tus datos personales y qué papel juegan los servicios de terceros."
    sections={[
      { h: "Datos que recogemos", p: "Recogemos los datos que nos facilitas al registrarte (nombre o alias, correo y preferencias de aprendizaje) y datos de uso para mejorar tu progreso." },
      { h: "Finalidad", p: "Usamos tus datos para prestarte el servicio educativo, personalizar tu ruta y, si lo consientes, enviarte nuestra newsletter." },
      { h: "Cookies y publicidad de terceros (Google AdSense)", p: [
        "Este sitio puede mostrar anuncios de Google AdSense. Proveedores externos, incluido Google, utilizan cookies para publicar anuncios en función de las visitas anteriores del usuario a este u otros sitios web.",
        "Google utiliza cookies publicitarias que le permiten a él y a sus socios mostrar anuncios basados en tu navegación. Puedes desactivar la publicidad personalizada visitando la Configuración de anuncios de Google."],
        list: ["Más información y opciones: Configuración de anuncios de Google (google.com/settings/ads).",
               "Inhabilitar cookies de terceros: (www.aboutads.info).",
               "Proveedor publicitario: Google AdSense (Google Ireland Ltd.). Política de privacidad de Google: policies.google.com/privacy."] },
      { h: "Analítica", p: "Usamos Google Analytics 4 con la IP anonimizada para medir de forma agregada el uso del sitio y mejorarlo; no lo utilizamos para publicidad. La publicidad solo usa cookies tras tu consentimiento mediante la CMP certificada de Google. Puedes oponerte a la analítica con el complemento tools.google.com/dlpage/gaoptout." },
      { h: "Responsable del tratamiento", p: "El responsable del tratamiento es el editor identificado en el aviso legal. Para cualquier consulta sobre tus datos puedes escribir a privacidad@hallosuiza.com." },
      { h: "Tus derechos", p: "Puedes exportar o eliminar tu cuenta en cualquier momento desde tu panel, y darte de baja de la newsletter cuando quieras. Para ejercer tus derechos o consultas sobre tus datos, escribe a privacidad@hallosuiza.com." },
    ]} />;
}

export function Cookies() {
  return <ContentPage overline="Legal" title="Política de cookies" crumb="Cookies"
    subtitle="Uso de cookies y tecnologías similares en Hallo Suiza."
    note="La analítica se usa de forma anonimizada para mejorar el sitio; puedes oponerte en cualquier momento (ver más abajo). La publicidad se activa únicamente con tu consentimiento, a través de la plataforma de consentimiento (CMP) certificada de Google (UE/EEE, Reino Unido y Suiza)."
    sections={[
      { h: "Cookies necesarias", p: "Imprescindibles para mantener tu sesión iniciada de forma segura. No requieren consentimiento." },
      { h: "Cookies de analítica", p: "Usamos Google Analytics 4 con la IP anonimizada para entender de forma agregada cómo se usa el sitio y mejorarlo. No se emplea para publicidad. Puedes oponerte con el complemento de inhabilitación de Google (tools.google.com/dlpage/gaoptout) o bloqueando las cookies en tu navegador." },
      { h: "Cookies de publicidad (Google AdSense)", p: [
        "Google y sus socios pueden usar cookies para mostrar anuncios personalizados según tu navegación.",
        "Estas cookies solo se cargan si das tu consentimiento en el mensaje de consentimiento de Google. Puedes cambiar tu elección en cualquier momento."],
        list: ["Gestionar preferencias de anuncios de Google: google.com/settings/ads",
               "Opt-out del sector: youronlinechoices.eu"] },
      { h: "Gestión de preferencias", p: "El consentimiento para publicidad se solicita a través de la plataforma de consentimiento (CMP) de Google. Para la analítica puedes oponerte con el complemento de inhabilitación de Google Analytics o borrando las cookies de tu navegador." },
    ]} />;
}

export function LegalNotice() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/site-config").then(({ data }) => setCfg(data)).catch(() => setCfg({})); }, []);
  const rows = [
    ["Nombre / empresa", cfg?.company_name], ["Responsable legal", cfg?.legal_responsible],
    ["Domicilio", cfg?.address], ["Identificación fiscal", cfg?.tax_id], ["Correo de contacto", cfg?.contact_email],
  ];
  return (
    <>
      <Seo title="Aviso legal" description="Aviso legal e información del editor de Hallo Suiza." />
      <PageHeader overline="Legal" title="Aviso legal" subtitle="Información sobre el editor responsable de este sitio web." />
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Aviso legal" }]} />
        <div className="rounded-lg border border-border bg-card divide-y divide-border">
          {rows.filter(([, val]) => val).map(([label, val]) => (
            <div key={label} className="flex items-center justify-between px-5 py-3.5 text-sm gap-4">
              <span className="font-medium">{label}</span>
              <span className="text-foreground text-right">{val}</span>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">Contenido educativo. No constituye asesoramiento legal, fiscal, migratorio ni médico. Consulta el organismo oficial competente para cada trámite.</p>
      </div>
    </>
  );
}

export function EditorialTeam() {
  return (
    <>
      <Seo title="Equipo editorial de Hallo Suiza" description="Quién elabora y revisa el contenido de Hallo Suiza y con qué método." />
      <PageHeader overline="Autoría" title="Equipo editorial de Hallo Suiza"
        subtitle="Todo el contenido lo elabora y revisa nuestro equipo editorial siguiendo un proceso de verificación con revisión humana." />
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12 space-y-8">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Equipo editorial" }]} />
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center"><Users size={28} className="text-primary" /></div>
          <div><h2 className="font-display font-bold text-xl">Equipo editorial de Hallo Suiza</h2><p className="text-sm text-muted-foreground">Contenido educativo para hispanohablantes en Suiza</p></div>
        </div>
        <section>
          <h2 className="font-display font-bold text-xl mb-2">Qué hacemos</h2>
          <p className="text-muted-foreground leading-relaxed">Creamos lecciones, ejercicios y guías centradas en situaciones reales de la vida y el trabajo en la Suiza germanófona. Priorizamos la utilidad, la claridad y la exactitud por encima del volumen.</p>
        </section>
        <section>
          <h2 className="font-display font-bold text-xl mb-2">Cómo verificamos</h2>
          <ul className="space-y-2 text-muted-foreground list-disc pl-5">
            <li>Revisión lingüística del alemán y de las traducciones.</li>
            <li>Comprobación de las respuestas de los ejercicios.</li>
            <li>Contraste de la información con fuentes oficiales suizas y fecha de comprobación visible.</li>
            <li>Ningún contenido se publica sin aprobación humana.</li>
          </ul>
        </section>
        <div className="flex flex-wrap gap-3">
          <Link to="/metodologia-editorial"><Button variant="outline" className="rounded-full">Metodología editorial</Button></Link>
          <Link to="/politica-de-correcciones"><Button variant="outline" className="rounded-full">Política de correcciones</Button></Link>
          <Link to="/guias"><Button className="rounded-full">Ver nuestras guías</Button></Link>
        </div>
      </div>
    </>
  );
}

export function Terms() {
  return <ContentPage overline="Legal" title="Términos y condiciones" crumb="Términos"
    subtitle="Condiciones de uso de Hallo Suiza."
    sections={[
      { h: "Servicio educativo", p: "Hallo Suiza ofrece contenido educativo para aprender alemán. Nuestras pruebas y certificados internos son orientativos y no oficiales." },
      { h: "Uso responsable", p: "Te comprometes a usar la plataforma de forma lícita y a no compartir credenciales ni abusar de los servicios." },
      { h: "Limitación", p: "El contenido no constituye asesoramiento legal, fiscal, migratorio ni médico. Para trámites, consulta el organismo oficial competente." },
    ]} />;
}

export function Affiliation() {
  return <ContentPage overline="Legal" title="Política de afiliación" crumb="Afiliación" noindex
    subtitle="Transparencia sobre posibles enlaces de afiliación."
    sections={[
      { h: "Transparencia", p: "Si en el futuro incluimos enlaces de afiliación, lo indicaremos claramente. Nunca recomendaremos algo solo por obtener una comisión." },
      { h: "Independencia editorial", p: "Nuestras recomendaciones se basan en la utilidad para el usuario, no en acuerdos comerciales." },
    ]} />;
}
